import React, { useState } from 'react';
import { Settings as SettingsIcon, Bell, Lock, Globe, Moon, Sun, Monitor, Save, Shield, Mail, Smartphone } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState({
    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    ideaSubmissionNotif: true,
    reviewNotif: true,
    systemUpdatesNotif: false,
    
    // Appearance Settings
    theme: 'light',
    compactMode: false,
    
    // Privacy Settings
    profileVisibility: 'public',
    showEmail: true,
    
    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: '30'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    // Simulate save
    setTimeout(() => {
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setSaveMessage('Settings saved successfully!');
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }, 500);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '600', color: 'var(--cares-text-2)', margin: '0 0 8px 0' }}>
            Settings
          </h1>
          <p style={{ color: 'var(--cares-text-label)', margin: 0 }}>
            Manage your application preferences and settings
          </p>
        </div>
        
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
            color: 'var(--cares-text-max)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1
          }}
        >
          <Save style={{ width: '16px', height: '16px' }} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Success Message */}
      {saveMessage && (
        <div style={{
          padding: '12px 16px',
          background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-green)',
          borderRadius: '8px',
          color: 'var(--cares-green)',
          marginBottom: '24px',
          fontSize: '14px'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Settings Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* Notification Settings */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Bell style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', margin: 0 }}>
              Notifications
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <SettingToggle
              label="Email Notifications"
              description="Receive notifications via email"
              checked={settings.emailNotifications}
              onChange={() => handleToggle('emailNotifications')}
              icon={<Mail style={{ width: '16px', height: '16px' }} />}
            />
            <SettingToggle
              label="Push Notifications"
              description="Receive push notifications in browser"
              checked={settings.pushNotifications}
              onChange={() => handleToggle('pushNotifications')}
              icon={<Smartphone style={{ width: '16px', height: '16px' }} />}
            />
            <SettingToggle
              label="Idea Submission Alerts"
              description="Get notified when new ideas are submitted"
              checked={settings.ideaSubmissionNotif}
              onChange={() => handleToggle('ideaSubmissionNotif')}
            />
            <SettingToggle
              label="Review Notifications"
              description="Get notified about review updates"
              checked={settings.reviewNotif}
              onChange={() => handleToggle('reviewNotif')}
            />
            <SettingToggle
              label="System Updates"
              description="Get notified about system maintenance and updates"
              checked={settings.systemUpdatesNotif}
              onChange={() => handleToggle('systemUpdatesNotif')}
            />
          </div>
        </div>

        {/* Appearance Settings */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Monitor style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', margin: 0 }}>
              Appearance
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
                Theme
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <ThemeButton
                  icon={<Sun style={{ width: '16px', height: '16px' }} />}
                  label="Light"
                  active={settings.theme === 'light'}
                  onClick={() => handleChange('theme', 'light')}
                />
                <ThemeButton
                  icon={<Moon style={{ width: '16px', height: '16px' }} />}
                  label="Dark"
                  active={settings.theme === 'dark'}
                  onClick={() => handleChange('theme', 'dark')}
                />
                <ThemeButton
                  icon={<Monitor style={{ width: '16px', height: '16px' }} />}
                  label="System"
                  active={settings.theme === 'system'}
                  onClick={() => handleChange('theme', 'system')}
                />
              </div>
            </div>

            <SettingToggle
              label="Compact Mode"
              description="Reduce spacing and padding for a more compact view"
              checked={settings.compactMode}
              onChange={() => handleToggle('compactMode')}
            />
          </div>
        </div>

        {/* Privacy Settings */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Globe style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', margin: 0 }}>
              Privacy
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
                Profile Visibility
              </label>
              <select
                value={settings.profileVisibility}
                onChange={(e) => handleChange('profileVisibility', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-text-2)',
                  cursor: 'pointer'
                }}
              >
                <option value="public">Public - Visible to all users</option>
                <option value="team">Team Only - Visible to team members</option>
                <option value="private">Private - Only visible to you</option>
              </select>
            </div>

            <SettingToggle
              label="Show Email Address"
              description="Display your email address on your profile"
              checked={settings.showEmail}
              onChange={() => handleToggle('showEmail')}
            />
          </div>
        </div>

        {/* Security Settings */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Shield style={{ width: '20px', height: '20px', color: 'var(--cares-blue)' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', margin: 0 }}>
              Security
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <SettingToggle
              label="Two-Factor Authentication"
              description="Add an extra layer of security to your account"
              checked={settings.twoFactorAuth}
              onChange={() => handleToggle('twoFactorAuth')}
              icon={<Lock style={{ width: '16px', height: '16px' }} />}
            />

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
                Session Timeout (minutes)
              </label>
              <select
                value={settings.sessionTimeout}
                onChange={(e) => handleChange('sessionTimeout', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-text-2)',
                  cursor: 'pointer'
                }}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// Toggle Component
const SettingToggle = ({ label, description, checked, onChange, icon }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderRadius: '8px',
    background: 'var(--cares-surface-board)',
    border: '1px solid var(--cares-line-card)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
      {icon && <div style={{ color: 'var(--cares-text-label)' }}>{icon}</div>}
      <div>
        <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cares-text-2)', marginBottom: '2px' }}>
          {label}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
          {description}
        </div>
      </div>
    </div>
    <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px', cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span style={{
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: checked ? 'var(--cares-blue)' : 'var(--cares-line-strong)',
        transition: '0.3s',
        borderRadius: '24px'
      }}>
        <span style={{
          position: 'absolute',
          content: '',
          height: '18px',
          width: '18px',
          left: checked ? '26px' : '3px',
          bottom: '3px',
          background: 'var(--cares-surface-card)',
          transition: '0.3s',
          borderRadius: '50%'
        }} />
      </span>
    </label>
  </div>
);

// Theme Button Component
const ThemeButton = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
      padding: '16px',
      border: `2px solid ${active ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
      borderRadius: '8px',
      background: active ? 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))' : 'var(--cares-surface-card)',
      color: active ? 'var(--cares-blue)' : 'var(--cares-text-label)',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
  >
    {icon}
    <span style={{ fontSize: '13px', fontWeight: '500' }}>{label}</span>
  </button>
);

export default Settings;

